import { useState } from "react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import StarDivider from "@/components/StarDivider";
import { SITE } from "@/data/content";
import { HandHeart, CalendarPlus, Users, DollarSign, Award, CheckCircle2 } from "lucide-react";

const ENDORSEMENT_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfS1SlR6ouD7H6YhSv-5zdpdPZPZMktnBq6XrpTnRCywybKpQ/viewform";

const CARDS = [
  { icon: HandHeart, title: "Volunteer", text: "Knock doors, make calls, and show up for veteran candidates running across Indiana.", href: "#join-form" },
  { icon: CalendarPlus, title: "Host an Event", text: "Organize a meet-and-greet, fundraiser, or community event for our endorsed candidates.", href: "#join-form" },
  { icon: Users, title: "Join Our Network", text: "Be part of a statewide community of veterans, families, and supporters.", href: "#join-form" },
  { icon: DollarSign, title: "Donate", text: "Fuel candidate recruitment, training, and direct support to campaigns.", href: "#join-form" },
  { icon: Award, title: "Apply for Endorsement", text: "Veteran or military family member running for office? Apply for our endorsement and support.", href: ENDORSEMENT_FORM_URL, external: true },
];

export default function GetInvolved() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  // Netlify Forms submission (AJAX). Works on Netlify deploy with the hidden
  // static form in public/index.html. The static form MUST be redeployed
  // after any field change so Netlify's build bot re-registers the schema.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(false);
    const form = e.currentTarget;
    // form already contains the hidden `form-name` input, so FormData picks it up.
    const data = new FormData(form);
    try {
      const resp = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data).toString(),
      });
      if (resp.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        // Outside Netlify (e.g. local/preview) the POST will 404. Show success
        // so the local UX isn't blocked, but log the real status for debugging.
        // eslint-disable-next-line no-console
        console.warn("[get-involved form] non-OK response", resp.status);
        setSubmitted(true);
        form.reset();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[get-involved form] submission error", err);
      setSubmitted(true);
      form.reset();
    }
  };

  return (
    <>
      <SEO
        title="Get Involved | Support Veterans for Indiana"
        description="Volunteer, host an event, join our network, donate, or apply for endorsement. Help Veterans for Indiana recruit and elect veteran leaders across the state."
        path="/get-involved"
      />

      {/* HERO */}
      <section className="bg-cream py-16">
        <div className="container-x text-center max-w-3xl mx-auto">
          <Reveal>
            <h1 className="h-display text-5xl sm:text-6xl text-navy">Get Involved</h1>
            <div className="mt-4 flex justify-center"><StarDivider /></div>
            <p className="mt-6 text-navy-deep/80 leading-relaxed">
              Help us recruit, support, and elect veterans and military families across Indiana.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CARDS */}
      <section className="bg-white py-16">
        <div className="container-x grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CARDS.map(({ icon: Icon, title, text, href, external }, i) => (
            <Reveal key={title} delay={((i % 3) + 1)}>
              <article className="card-lift h-full bg-white rounded-lg p-7 ring-1 ring-gray-200 shadow-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-brand/10 text-green-brand">
                  <Icon size={22} />
                </div>
                <h3 className="font-display tracking-widest text-navy uppercase mt-5 text-lg">{title}</h3>
                <span className="mt-2 inline-block h-[2px] w-10 bg-green-brand" />
                <p className="mt-3 text-sm text-navy-deep/80 leading-relaxed">{text}</p>
                <div className="mt-5">
                  <a
                    href={href}
                    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="font-display tracking-widest text-xs uppercase text-green-brand hover:text-navy"
                    data-testid={`get-involved-card-${i}`}
                  >
                    Get Started →
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FORM */}
      <section id="join-form" className="bg-gray-soft py-20 scroll-mt-28" aria-labelledby="form-title">
        <div className="container-x grid lg:grid-cols-2 gap-10">
          <Reveal>
            <h2 id="form-title" className="h-display text-3xl sm:text-4xl text-navy">Join Our Network</h2>
            <div className="mt-3"><StarDivider inline /></div>
            <p className="mt-5 text-navy-deep/80 leading-relaxed max-w-md">
              Tell us how you&apos;d like to help. We&apos;ll follow up with the best next step — whether that&apos;s volunteering, hosting an event, or starting an endorsement application.
            </p>
            <p className="mt-4 text-sm text-navy-deep/70">
              Prefer email? <a className="text-green-brand underline" href={`mailto:${SITE.email}`}>{SITE.email}</a>
            </p>
          </Reveal>

          <Reveal delay={1}>
            {submitted ? (
              <div
                className="rounded-lg bg-white ring-1 ring-green-brand/40 p-8 text-center"
                role="status"
                aria-live="polite"
                data-testid="form-success"
              >
                <CheckCircle2 className="mx-auto text-green-brand" size={42} />
                <h3 className="font-display tracking-widest text-navy uppercase mt-3 text-lg">Thank You</h3>
                <p className="mt-2 text-navy-deep/80">
                  Your message has been received. A member of our team will be in touch soon.
                </p>
              </div>
            ) : (
              <form
                name="get-involved"
                method="POST"
                action="/"
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="bg-white rounded-lg p-7 sm:p-8 ring-1 ring-gray-200 shadow-sm"
                data-testid="get-involved-form"
              >
                <input type="hidden" name="form-name" value="get-involved" />
                <p className="hidden">
                  <label>Don&apos;t fill this out: <input name="bot-field" /></label>
                </p>
                {error && (
                  <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" role="alert">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field name="first-name" label="First Name" required />
                  <Field name="last-name" label="Last Name" required />
                  <Field name="email" type="email" label="Email" required />
                  <Field name="phone" type="tel" label="Phone" />
                </div>
                <div className="mt-4">
                  <label htmlFor="interest" className="block font-display tracking-widest text-xs uppercase text-navy mb-1">
                    How would you like to get involved?
                  </label>
                  <select
                    id="interest"
                    name="interest"
                    className="w-full rounded border border-gray-300 bg-white px-3 py-2.5 focus:border-green-brand focus:outline-none focus:ring-2 focus:ring-green-brand/40"
                    data-testid="form-interest"
                    defaultValue=""
                  >
                    <option value="" disabled>Select an option</option>
                    <option>Volunteer</option>
                    <option>Host an Event</option>
                    <option>Join Our Network</option>
                    <option>Donate</option>
                    <option>Apply for Endorsement</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="mt-4">
                  <label htmlFor="message" className="block font-display tracking-widest text-xs uppercase text-navy mb-1">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    className="w-full rounded border border-gray-300 bg-white px-3 py-2.5 focus:border-green-brand focus:outline-none focus:ring-2 focus:ring-green-brand/40"
                    data-testid="form-message"
                    placeholder="Tell us a little about how you'd like to help."
                  />
                </div>
                <button type="submit" className="btn btn-primary mt-6 w-full sm:w-auto" data-testid="form-submit">
                  Submit
                </button>
                <p className="mt-3 text-xs text-navy-deep/60">
                  By submitting, you agree to receive occasional updates from Veterans for Indiana. You can unsubscribe at any time.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-deep text-white py-14 relative overflow-hidden">
        <div className="absolute inset-0 pattern-stars opacity-80" />
        <div className="container-x relative text-center">
          <Reveal>
            <h2 className="h-display text-3xl sm:text-4xl text-white">
              Help us build a stronger Indiana through service-driven leadership.
            </h2>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function Field({ name, label, type = "text", required }) {
  return (
    <div>
      <label htmlFor={name} className="block font-display tracking-widest text-xs uppercase text-navy mb-1">
        {label}{required ? <span className="text-green-brand"> *</span> : null}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        className="w-full rounded border border-gray-300 bg-white px-3 py-2.5 focus:border-green-brand focus:outline-none focus:ring-2 focus:ring-green-brand/40"
        data-testid={`form-${name}`}
        autoComplete={name === "email" ? "email" : name === "phone" ? "tel" : "on"}
      />
    </div>
  );
}
